import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate, useLocation } from "react-router-dom";

export default function InviteInvalid() {
  const navigate = useNavigate();
  const location = useLocation();

  // pull any ?org=... from the current URL (for example if user hit
  // /portal/invite?org=abc&client=...&invite=... but it was expired)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const orgParam = searchParams.get("org") || "";

  const handleRequestNewLink = () => {
    // If we know which org this expired link belonged to, preserve it.
    // That lets /portal/recover prefill (and then send it to the backend).
    if (orgParam) {
      navigate(`/portal/recover?org=${encodeURIComponent(orgParam)}`);
    } else {
      navigate("/portal/recover");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 20% 20%, #ffffff 0%, #eef2ff 60%, #e5e7eb 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow:
            "0 20px 40px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid #E5E7EB",
            background:
              "linear-gradient(90deg, rgba(185,28,28,0.08) 0%, rgba(0,0,0,0) 60%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ErrorOutlineIcon
              sx={{
                color: "#B91C1C",
                fontSize: 28,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: "#111827",
                lineHeight: 1.3,
              }}
            >
              Link Expired or Invalid
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <CardContent sx={{ pt: 3, pb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#374151",
              fontSize: 15,
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            The secure link you used is no longer valid.
            For your protection, access links expire automatically
            or can only be used once.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#4B5563",
              fontSize: 14,
              lineHeight: 1.5,
              mb: 3,
            }}
          >
            You can request a fresh 2-hour access link below.
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={handleRequestNewLink}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              borderRadius: 1.5,
              py: 1.2,
              backgroundColor: "#1e40af",
              ":hover": { backgroundColor: "#1e3a8a" },
              boxShadow:
                "0 8px 16px rgba(30,64,175,0.35), 0 2px 4px rgba(0,0,0,0.08)",
            }}
          >
            Request New Secure Link
          </Button>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: "#9CA3AF",
              lineHeight: 1.5,
              fontSize: 12,
            }}
          >
            Protected by Kinova Client Portal.
            <br />
            <span style={{ color: "#6B7280" }}>
              If you're still having trouble, reply to the original email or
              text message you received.
            </span>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}